import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { API_CONFIG, API_URLS } from '../../../config/api';
import MediaSelector from '../MediaSelector';
import { formatPrice, normalizeUrl } from '../../../utils/format';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  Search, 
  Filter,
  Building2,
  X,
  ExternalLink,
  Save,
  Upload,
  Camera,
  Loader2
} from 'lucide-react';

const AdminCorporateDesigns = () => {
  const [designs, setDesigns] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    category_id: null,
    category_ids: [],
    image_url: '',
    demo_url: '',
    admin_url: '',
    purchase_url: '',
    price: '',
    tags: [],
    sort_order: 0,
    is_active: true
  });
  const [tagInput, setTagInput] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [capturingScreenshots, setCapturingScreenshots] = useState(new Set());
  const [bulkScreenshotProgress, setBulkScreenshotProgress] = useState(null);

  useEffect(() => {
    fetchDesigns();
    fetchCategories();
  }, []);

  const fetchDesigns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCorporateDesigns`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setDesigns(data.data || []);
      } else {
        console.error('API Error:', data.message);
      }
    } catch (error) {
      console.error('Error fetching designs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getCorporateDesignCategories`);
      const data = await response.json();
      
      if (data.status === 'success') {
        setCategories(data.data || []);
      } else {
        console.error('API Error:', data.message);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const generateSlug = (name) => {
    const turkishMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G',
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U'
    };
    
    return name
      .split('')
      .map(char => turkishMap[char] || char)
      .join('')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameChange = (name) => {
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const captureScreenshot = async (design, force = false) => {
    const url = design.demo_url || design.admin_url;
    if (!url) {
      alert('Bu tasarım için demo URL veya admin URL bulunamadı');
      return;
    }

    const normalizedUrl = normalizeUrl(url);
    setCapturingScreenshots(prev => new Set(prev).add(design.id));
    
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          design_id: design.id,
          design_type: 'corporate',
          url: normalizedUrl,
          force: force
        })
      });

      const result = await response.json();
      
      if (result.status === 'success') {
        setDesigns(prevDesigns => 
          prevDesigns.map(d => 
            d.id === design.id 
              ? { ...d, image_url: result.data.image_url }
              : d
          )
        );
        alert('Ekran görüntüsü başarıyla alındı ve kaydedildi!');
      } else {
        alert('Ekran görüntüsü alınamadı: ' + (result.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Screenshot capture error:', error);
      alert('Ekran görüntüsü alınırken bir hata oluştu: ' + error.message);
    } finally {
      setCapturingScreenshots(prev => {
        const newSet = new Set(prev);
        newSet.delete(design.id);
        return newSet;
      });
    }
  };

  const captureBulkScreenshots = async () => {
    const designsToCapture = designs.filter(design => {
      const url = design.demo_url || design.admin_url;
      return url;
    });

    if (designsToCapture.length === 0) {
      alert('Ekran görüntüsü alınabilecek tasarım bulunamadı (demo URL veya admin URL gerekli)');
      return;
    }

    if (!confirm(`${designsToCapture.length} tasarım için ekran görüntüsü alınacak. Devam etmek istiyor musunuz?`)) {
      return;
    }

    setBulkScreenshotProgress({
      total: designsToCapture.length,
      current: 0,
      completed: 0,
      failed: 0
    });

    for (let i = 0; i < designsToCapture.length; i++) {
      const design = designsToCapture[i];
      setBulkScreenshotProgress(prev => ({
        ...prev,
        current: i + 1
      }));

      try {
        const url = design.demo_url || design.admin_url;
        const normalizedUrl = normalizeUrl(url);
        const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=captureDesignScreenshot`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            design_id: design.id,
            design_type: 'corporate',
            url: normalizedUrl,
            force: false
          })
        });

        const result = await response.json();
        
        if (result.status === 'success') {
          setDesigns(prevDesigns => 
            prevDesigns.map(d => 
              d.id === design.id 
                ? { ...d, image_url: result.data.image_url }
                : d
            )
          );
          setBulkScreenshotProgress(prev => ({
            ...prev,
            completed: prev.completed + 1
          }));
        } else {
          setBulkScreenshotProgress(prev => ({
            ...prev,
            failed: prev.failed + 1
          }));
        }
      } catch (error) {
        console.error(`Screenshot capture error for design ${design.id}:`, error);
        setBulkScreenshotProgress(prev => ({
          ...prev,
          failed: prev.failed + 1
        }));
      }

      if (i < designsToCapture.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    setBulkScreenshotProgress(prev => {
      alert(`Toplu ekran görüntüsü alma tamamlandı!\nBaşarılı: ${prev.completed}\nBaşarısız: ${prev.failed}`);
      return null;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        category_ids: formData.category_ids || [],
        id: editingDesign?.id
      };

      const action = editingDesign ? 'updateCorporateDesign' : 'createCorporateDesign';
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.status === 'success') {
        fetchDesigns();
        handleCloseModal();
        alert(editingDesign ? 'Tasarım güncellendi!' : 'Tasarım oluşturuldu!');
      } else {
        alert('Hata: ' + (data.message || 'Bilinmeyen hata'));
      }
    } catch (error) {
      console.error('Error saving design:', error);
      alert('Tasarım kaydedilemedi!');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu tasarımı silmek istediğinizden emin misiniz?')) return;
    
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteCorporateDesign&id=${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        fetchDesigns();
        alert('Tasarım silindi!');
      }
    } catch (error) {
      console.error('Error deleting design:', error);
      alert('Tasarım silinemedi!');
    }
  };

  const handleDeleteAll = async () => {
    const designCount = designs.length;
    if (designCount === 0) {
      alert('Silinecek tasarım bulunmuyor.');
      return;
    }
    
    const confirmMessage = `Tüm kurumsal tasarımları (${designCount} adet) silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`;
    if (window.confirm(confirmMessage)) {
      // Double confirmation for safety
      if (window.confirm('Bu işlem gerçekten geri alınamaz. Devam etmek istediğinizden emin misiniz?')) {
        try {
          const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=deleteAllCorporateDesigns`, {
            method: 'POST'
          });
          const result = await response.json();
          if (result.status === 'success') {
            alert(result.message || 'Tüm kurumsal tasarımlar başarıyla silindi');
            fetchDesigns();
          } else {
            alert('Hata: ' + result.message);
          }
        } catch (error) {
          console.error('Error deleting all designs:', error);
          alert('Tasarımlar silinirken bir hata oluştu');
        }
      }
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=toggleCorporateDesignStatus&id=${id}`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        fetchDesigns();
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleEdit = (design) => {
    setEditingDesign(design);
    
    // Parse category_ids (can be array or single value for backward compatibility)
    let parsedCategoryIds = [];
    if (design.category_ids && Array.isArray(design.category_ids)) {
      parsedCategoryIds = design.category_ids.filter(id => id); // Remove empty values
    } else if (design.category_id) {
      // Backward compatibility: single category_id
      parsedCategoryIds = [design.category_id];
    }
    
    setFormData({
      name: design.name || '',
      slug: design.slug || '',
      description: design.description || '',
      category_id: parsedCategoryIds.length > 0 ? parsedCategoryIds[0] : null,
      category_ids: parsedCategoryIds,
      image_url: design.image_url || '',
      demo_url: design.demo_url || '',
      admin_url: design.admin_url || '',
      purchase_url: design.purchase_url || '',
      price: design.price || '',
      tags: design.tags ? (Array.isArray(design.tags) ? design.tags : JSON.parse(design.tags)) : [],
      sort_order: design.sort_order || 0,
      is_active: design.is_active !== false
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDesign(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      category_id: null,
      category_ids: [],
      image_url: '',
      demo_url: '',
      admin_url: '',
      purchase_url: '',
      tags: [],
      sort_order: 0,
      is_active: true
    });
    setTagInput('');
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      alert('Lütfen .xlsx veya .xls formatında bir dosya seçin');
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=importCorporateDesigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (result.status === 'success') {
        alert(`Import başarılı! ${result.inserted || 0} tasarım eklendi, ${result.updated || 0} tasarım güncellendi.`);
        fetchDesigns();
      } else {
        alert(result.message || 'Import sırasında bir hata oluştu');
      }
    } catch (error) {
      console.error('Import error:', error);
      alert('Import sırasında bir hata oluştu: ' + error.message);
    } finally {
      setIsImporting(false);
      // Reset file input
      e.target.value = '';
    }
  };

  const filteredDesigns = designs.filter(design => {
    const matchesSearch = design.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         design.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || design.category_id == selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id == categoryId);
    return category?.name || '-';
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-2 break-words">
            <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 flex-shrink-0" />
            <span className="break-words">Kurumsal Site Tasarımları</span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 break-words">
            Kurumsal web sitesi tasarımlarını yönetin
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button 
            onClick={() => document.getElementById('excel-file-input-corporate')?.click()} 
            className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
            disabled={isImporting}
          >
            <Upload className="w-4 h-4 mr-2" />
            {isImporting ? 'Import Ediliyor...' : 'Excel Import'}
          </Button>
          <input
            id="excel-file-input-corporate"
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={handleExcelImport}
          />
          {designs.length > 0 && (
            <Button 
              variant="destructive"
              onClick={handleDeleteAll}
              className="w-full sm:w-auto"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Tümünü Sil
            </Button>
          )}
          <Button 
            onClick={() => {
              handleCloseModal();
              setIsModalOpen(true);
            }}
            className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni Tasarım Ekle
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 sm:pt-6 p-4 sm:p-6">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Tasarım ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Tüm Kategoriler</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <Button 
                onClick={captureBulkScreenshots} 
                size="sm"
                variant="outline"
                disabled={bulkScreenshotProgress !== null}
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              >
                {bulkScreenshotProgress ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    İşleniyor ({bulkScreenshotProgress.current}/{bulkScreenshotProgress.total})
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4 mr-2" />
                    Toplu Ekran Görüntüsü Al
                  </>
                )}
              </Button>
            </div>
            {bulkScreenshotProgress && (
              <div className="pt-4 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-700">İlerleme:</span>
                  <span className="text-sm font-medium text-blue-700">
                    {bulkScreenshotProgress.completed} başarılı, {bulkScreenshotProgress.failed} başarısız
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(bulkScreenshotProgress.current / bulkScreenshotProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Designs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <p className="mt-2 text-gray-600">Yükleniyor...</p>
          </div>
        ) : filteredDesigns.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Henüz tasarım eklenmemiş</p>
          </div>
        ) : (
          filteredDesigns.map(design => (
            <Card key={design.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                <img
                  src={design.image_url}
                  alt={design.name}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/800x500/6b46c1/ffffff?text=Görsel+Yok'; }}
                />
              </div>
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 flex-1 text-sm sm:text-base break-words">{design.name}</h3>
                  <Badge className={`flex-shrink-0 text-xs ${design.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {design.is_active ? 'Aktif' : 'Pasif'}
                  </Badge>
                </div>
                
                <div className="text-xs sm:text-sm text-gray-600 space-y-1 mb-3">
                  <div className="break-words">
                    <span className="font-medium">Kategori:</span> {getCategoryName(design.category_id)}
                  </div>
                  {design.price && (
                    <div className="break-words">
                      <span className="font-medium">Fiyat:</span> <span className="text-purple-600 font-bold">{formatPrice(design.price)}</span>
                    </div>
                  )}
                  {design.demo_url && (
                    <div className="text-xs break-words">
                      <span className="font-medium">Demo:</span> <a href={normalizeUrl(design.demo_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{design.demo_url}</a>
                    </div>
                  )}
                  {design.admin_url && (
                    <div className="text-xs break-words">
                      <span className="font-medium">Admin:</span> <a href={normalizeUrl(design.admin_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{design.admin_url}</a>
                    </div>
                  )}
                  {design.purchase_url && (
                    <div className="text-xs break-words">
                      <span className="font-medium">Satın Al:</span>{' '}
                      <a
                        href={normalizeUrl(design.purchase_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline break-all"
                      >
                        {design.purchase_url}
                      </a>
                    </div>
                  )}
                </div>

                {design.tags && design.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {design.tags.slice(0, 3).map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs break-words">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(design)}
                      className="flex-1 sm:flex-initial w-full sm:w-auto"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      <span className="text-xs sm:text-sm">Düzenle</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleToggleStatus(design.id)}
                      className={`${design.is_active ? 'text-orange-600' : 'text-green-600'} w-full sm:w-auto flex-1 sm:flex-initial`}
                    >
                      {design.is_active ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(design.id)}
                      className="text-red-600 w-full sm:w-auto flex-1 sm:flex-initial"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  {(design.demo_url || design.admin_url) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => captureScreenshot(design, false)}
                      disabled={capturingScreenshots.has(design.id)}
                      className="w-full bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                    >
                      {capturingScreenshots.has(design.id) ? (
                        <>
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          <span className="text-xs sm:text-sm">Alınıyor...</span>
                        </>
                      ) : (
                        <>
                          <Camera className="w-3 h-3 mr-1" />
                          <span className="text-xs sm:text-sm">Ekran Görüntüsü Al</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit/Create Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-[95vw] sm:max-w-md md:max-w-2xl w-full max-h-[90vh] overflow-y-auto my-auto">
            <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <h2 className="text-base sm:text-xl font-bold text-gray-900 break-words">
                {editingDesign ? 'Tasarımı Düzenle' : 'Yeni Tasarım Ekle'}
              </h2>
              <Button variant="ghost" size="sm" onClick={handleCloseModal} className="flex-shrink-0">
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div>
                <Label className="text-sm">Tasarım Adı *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  required
                  placeholder="ör: Premium Kurumsal"
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-sm">Slug *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  required
                  placeholder="premium-kurumsal"
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-sm">Kategoriler</Label>
                <div className="flex flex-wrap gap-2 p-3 border border-gray-300 rounded-lg bg-gray-50 max-h-48 overflow-y-auto">
                  {categories.length === 0 ? (
                    <p className="text-xs sm:text-sm text-gray-500">Önce kategori oluşturun</p>
                  ) : (
                    categories.filter(cat => cat.is_active !== false).map((category) => {
                      const isSelected = (formData.category_ids || []).includes(category.id);
                      return (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => {
                            const currentIds = formData.category_ids || [];
                            if (isSelected) {
                              const newIds = currentIds.filter(id => id !== category.id);
                              setFormData({ ...formData, category_ids: newIds, category_id: newIds[0] || null });
                            } else {
                              const newIds = [...currentIds, category.id];
                              setFormData({ ...formData, category_ids: newIds, category_id: newIds[0] || null });
                            }
                          }}
                          className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                            isSelected
                              ? 'bg-purple-600 text-white ring-2 ring-purple-500'
                              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                          }`}
                        >
                          {category.name}
                        </button>
                      );
                    })
                  )}
                </div>
                {formData.category_ids && formData.category_ids.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1 break-words">
                    Seçili: {formData.category_ids.map(id => {
                      const cat = categories.find(c => c.id === id);
                      return cat ? cat.name : id;
                    }).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <Label className="text-sm">Açıklama</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  placeholder="Tasarım hakkında kısa açıklama..."
                  className="w-full"
                />
              </div>

              <div>
                <MediaSelector
                  label="Görsel URL *"
                  value={formData.image_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                  placeholder="Medya kütüphanesinden seçin veya URL girin..."
                />
              </div>

              <div>
                <Label className="text-sm">Demo URL</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={formData.demo_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, demo_url: e.target.value }))}
                    placeholder="https://demo.example.com"
                    className="flex-1 min-w-0"
                  />
                  {formData.demo_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open(formData.demo_url, '_blank')}
                      className="flex-shrink-0 whitespace-nowrap"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm">Admin URL</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={formData.admin_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, admin_url: e.target.value }))}
                    placeholder="https://admin.example.com"
                    className="flex-1 min-w-0"
                  />
                  {formData.admin_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open(formData.admin_url, '_blank')}
                      className="flex-shrink-0 whitespace-nowrap"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm">Satın Al URL</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Input
                    value={formData.purchase_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, purchase_url: e.target.value }))}
                    placeholder="https://satis.example.com"
                    className="flex-1 min-w-0"
                  />
                  {formData.purchase_url && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => window.open(normalizeUrl(formData.purchase_url), '_blank')}
                      className="flex-shrink-0 whitespace-nowrap"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-sm">Fiyat (₺)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="0.00"
                  className="w-full"
                />
              </div>

              <div>
                <Label className="text-sm">Etiketler</Label>
                <div className="flex flex-col sm:flex-row gap-2 mb-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Etiket ekle..."
                    className="flex-1 min-w-0"
                  />
                  <Button type="button" onClick={addTag} variant="outline" className="flex-shrink-0 whitespace-nowrap">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="gap-1 text-xs break-words">
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer flex-shrink-0"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm">Sıra</Label>
                <Input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                  min="0"
                  className="w-full"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 flex-shrink-0"
                />
                <Label htmlFor="is_active" className="cursor-pointer text-sm">Aktif</Label>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-purple-600 hover:bg-purple-700 w-full sm:w-auto">
                  <Save className="w-4 h-4 mr-2" />
                  {editingDesign ? 'Güncelle' : 'Oluştur'}
                </Button>
                <Button type="button" variant="outline" onClick={handleCloseModal} className="flex-1 w-full sm:w-auto">
                  İptal
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCorporateDesigns;


